import React, { createContext, useCallback, useState, useMemo } from "react";
import { SearchLogDto } from "../Models/SearchLogDto";

const DATE_FILTERS = {
  SINCE_YESTERDAY: "sinceYesterday",
  SINCE_LAST_WEEK: "sinceLastWeek",
  SINCE_LAST_MONTH: "sinceLastMonth",
  PAST_ENTRIES: "pastEntries",
};

interface ISlackSearchFiltersContext {
  dateFilter: string[];
  setDateFilter: React.Dispatch<React.SetStateAction<string[]>>;
  searchInput: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  groups: string[];
  setGroups: React.Dispatch<React.SetStateAction<string[]>>;
  composeSearchParameters: () => SearchLogDto;
}

export const SlackSearchFiltersContext = createContext<
  ISlackSearchFiltersContext | undefined
>(undefined);

export const SearchFiltersProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dateFilter, setDateFilter] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [groups, setGroups] = useState<string[]>([]);

  const currentDate = useMemo(() => new Date(), []);

  const setToStartOfDay = useCallback((d: Date): Date => {
    const newDate = new Date(d);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }, []);

  const toDateOnly = useCallback((d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const getDateFilters = useCallback(() => {
    const filters: Record<
      string,
      { StartDateUTC: string | null; EndDateUTC: string | null }
    > = {};

    if (
      dateFilter.includes(DATE_FILTERS.SINCE_YESTERDAY) ||
      dateFilter.length === 0
    ) {
      const end = setToStartOfDay(currentDate);
      const start = new Date(end);
      start.setDate(start.getDate() - 1);
      filters.yesterdayFilter = {
        StartDateUTC: toDateOnly(start),
        EndDateUTC: toDateOnly(end),
      };
    }

    if (
      dateFilter.includes(DATE_FILTERS.SINCE_LAST_WEEK) ||
      dateFilter.length === 0
    ) {
      const end = setToStartOfDay(new Date(currentDate));
      end.setDate(end.getDate() - 1);
      const start = new Date(end);
      start.setDate(start.getDate() - 6);
      filters.lastWeekFilter = {
        StartDateUTC: toDateOnly(start),
        EndDateUTC: toDateOnly(end),
      };
    }

    if (
      dateFilter.includes(DATE_FILTERS.SINCE_LAST_MONTH) ||
      dateFilter.length === 0
    ) {
      const end = setToStartOfDay(new Date(currentDate));
      end.setDate(end.getDate() - 1);
      const start = new Date(end);
      start.setMonth(start.getMonth() - 1);
      filters.lastMonthFilter = {
        StartDateUTC: toDateOnly(start),
        EndDateUTC: toDateOnly(end),
      };
    }

    if (
      dateFilter.includes(DATE_FILTERS.PAST_ENTRIES) ||
      dateFilter.length === 0
    ) {
      const end = setToStartOfDay(new Date(currentDate));
      end.setDate(end.getDate() - 1);
      const start = new Date(1, 0, 1);
      filters.pastLogsFilter = {
        StartDateUTC: toDateOnly(start),
        EndDateUTC: toDateOnly(end),
      };
    }

    return filters;
  }, [currentDate, dateFilter, setToStartOfDay, toDateOnly]);

  const composeSearchParameters = useCallback((): SearchLogDto => {
    const dateFilters = getDateFilters();
    return {
      Groups: groups,
      YesterdayFilter: dateFilters.yesterdayFilter || {
        StartDateUTC: null,
        EndDateUTC: null,
      },
      LastWeekFilter: dateFilters.lastWeekFilter || {
        StartDateUTC: null,
        EndDateUTC: null,
      },
      LastMonthFilter: dateFilters.lastMonthFilter || {
        StartDateUTC: null,
        EndDateUTC: null,
      },
      PastLogsFilter: dateFilters.pastLogsFilter || {
        StartDateUTC: null,
        EndDateUTC: null,
      },
      SortingOptions: [],
      SearchTerm: searchInput,
    };
  }, [getDateFilters, groups, searchInput]);

  return (
    <SlackSearchFiltersContext.Provider
      value={{
        dateFilter,
        setDateFilter,
        searchInput,
        setSearchInput,
        groups,
        setGroups,
        composeSearchParameters,
      }}
    >
      {children}
    </SlackSearchFiltersContext.Provider>
  );
};
